import React from 'react';
import { Link } from 'react-router-dom';
import {
  Palette,
  Table,
  Image,
  FileText,
  Layers,
  Sparkles,
  ArrowRight,
  Settings,
  Download,
  Edit3,
  Zap,
  Target,
  Users,
  BarChart3
} from 'lucide-react';

export const Studio: React.FC = () => {
  const studioTools = [
    {
      path: '/elevation/icon-customizer',
      label: 'Icon Customizer',
      icon: Sparkles,
      color: 'primary',
      description: 'Create and customize icons for your projects with our intuitive design tools',
      status: 'ready',
      category: 'Visual Assets'
    },
    {
      path: '/elevation/table-designer',
      label: 'Table Designer',
      icon: Table,
      color: 'sage',
      description: 'Design beautiful, responsive tables with advanced formatting and styling options',
      status: 'ready',
      category: 'Data Visualization'
    },
    {
      path: '/elevation/studio/image-editor',
      label: 'Image Editor',
      icon: Image,
      color: 'amber',
      description: 'Professional image editing and optimization tools for web and print',
      status: 'development',
      category: 'Visual Assets'
    },
    {
      path: '/elevation/studio/document-builder',
      label: 'Document Builder',
      icon: FileText,
      color: 'coral',
      description: 'Create professional documents, reports, and presentations with templates',
      status: 'development',
      category: 'Content Creation'
    },
    {
      path: '/elevation/studio/layout-designer',
      label: 'Layout Designer',
      icon: Layers,
      color: 'ocean',
      description: 'Design responsive layouts and wireframes for web and mobile applications',
      status: 'development',
      category: 'Layout & Design'
    },
    {
      path: '/elevation/studio/color-palette',
      label: 'Color Palette Generator',
      icon: Palette,
      color: 'terracotta',
      description: 'Generate harmonious color palettes and test accessibility compliance',
      status: 'development',
      category: 'Visual Assets'
    }
  ];

  const assetPipeline = [
    {
      stage: 'Design',
      description: 'Create visual assets and design elements',
      tools: ['Icon Customizer', 'Image Editor', 'Color Palette Generator'],
      color: 'primary'
    },
    {
      stage: 'Structure',
      description: 'Build layouts and organize content',
      tools: ['Table Designer', 'Layout Designer', 'Document Builder'],
      color: 'sage'
    },
    {
      stage: 'Optimize',
      description: 'Refine and prepare for deployment',
      tools: ['Asset Optimizer', 'Export Manager', 'Quality Checker'],
      color: 'amber'
    },
    {
      stage: 'Deploy',
      description: 'Integrate and publish assets',
      tools: ['Asset Manager', 'CDN Uploader', 'Version Control'],
      color: 'coral'
    }
  ];

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'primary': return { bg: 'bg-consulting-slate-blue-50', border: 'border-consulting-slate-blue-200', text: 'text-consulting-slate-blue-800', hoverBg: 'hover:bg-consulting-slate-blue-100', accent: 'text-consulting-slate-blue-600' };
      case 'sage': return { bg: 'bg-sage-50', border: 'border-sage-200', text: 'text-sage-800', hoverBg: 'hover:bg-sage-100', accent: 'text-sage-600' };
      case 'amber': return { bg: 'bg-[var(--mustard-dim)]', border: 'border-[var(--mustard)]', text: 'text-[var(--mustard)]', hoverBg: 'hover:bg-[var(--mustard-dim)]', accent: 'text-[var(--mustard)]' };
      case 'coral': return { bg: 'bg-coral-50', border: 'border-coral-200', text: 'text-coral-800', hoverBg: 'hover:bg-coral-100', accent: 'text-coral-600' };
      case 'ocean': return { bg: 'bg-ocean-50', border: 'border-ocean-200', text: 'text-ocean-800', hoverBg: 'hover:bg-ocean-100', accent: 'text-ocean-600' };
      case 'terracotta': return { bg: 'bg-terracotta-50', border: 'border-terracotta-200', text: 'text-terracotta-800', hoverBg: 'hover:bg-terracotta-100', accent: 'text-terracotta-600' };
      default: return { bg: 'bg-[var(--surface)]', border: 'border-[var(--rule)]', text: 'text-[var(--text-pri)]', hoverBg: 'hover:bg-[var(--hover-tint)]', accent: 'text-[var(--text-sec)]' };
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="h-16 w-16 bg-gradient-to-br from-consulting-slate-blue-500 to-sage-500 flex items-center justify-center shadow-lg">
          <Palette className="h-10 w-10 text-white" />
        </div>
        <div>
          <h1 className="text-4xl font-bold text-charcoal-900">Studio Engine</h1>
          <p className="text-lg text-charcoal-600">Professional asset creation and design tools for Peak Priorities</p>
        </div>
      </div>

      {/* Engine Overview */}
      <div className="bg-gradient-to-r from-consulting-slate-blue-50 to-sage-50 p-8 border-2 border-consulting-slate-blue-200">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-4">Asset Creation Pipeline</h2>
        <p className="text-charcoal-700 mb-6 leading-relaxed">
          The Studio Engine powers our complete asset creation pipeline, from initial design concepts 
          to production-ready deliverables. It provides professional-grade tools for creating, 
          optimizing, and managing all visual and structural assets used throughout the Peak Priorities system.
        </p>
        
        {/* Pipeline Stages */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {assetPipeline.map((stage, index) => {
            const colors = getColorClasses(stage.color);
            return (
              <div key={stage.stage} className={`${colors.bg} ${colors.border} border-2  p-4`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-8 h-8 ${colors.accent} bg-[var(--bg-raised)] rounded-full flex items-center justify-center text-sm font-bold`}>
                    {index + 1}
                  </div>
                  <h3 className={`font-bold ${colors.text}`}>{stage.stage}</h3>
                </div>
                <p className={`text-sm ${colors.text} mb-2`}>{stage.description}</p>
                <div className="space-y-1">
                  {stage.tools.map((tool, toolIndex) => (
                    <div key={toolIndex} className={`text-xs ${colors.text} opacity-75`}>
                      • {tool}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-[var(--bg-raised)] p-4 border border-consulting-slate-blue-200">
          <h3 className="font-bold text-consulting-slate-blue-900 mb-2">Studio Engine Capabilities</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-charcoal-700">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-[var(--mustard)]" />
              <span>Real-time Preview</span>
            </div>
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-sage-600" />
              <span>Brand Consistency</span>
            </div>
            <div className="flex items-center gap-2">
              <Download className="h-4 w-4 text-consulting-slate-blue-600" />
              <span>Multi-format Export</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-coral-600" />
              <span>Team Collaboration</span>
            </div>
          </div>
        </div>
      </div>

      {/* Studio Tools Grid */}
      <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-[var(--rule)] p-8">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-6">Studio Tools</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {studioTools.map((tool) => {
            const Icon = tool.icon;
            const colors = getColorClasses(tool.color);
            return (
              <Link
                key={tool.path}
                to={tool.path}
                className={`
                  ${colors.bg} ${colors.border} border-2  p-6 flex flex-col justify-between
                  ${colors.hoverBg} hover:shadow-lg transition-all duration-200 group
                `}
              >
                <div className="flex items-center justify-between mb-4">
                  <Icon className={`h-10 w-10 ${colors.accent} group-hover:scale-110 transition-transform`} />
                  <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                    tool.status === 'ready' ? 'bg-sage-100 text-sage-800' : 'bg-[var(--mustard-dim)] text-[var(--mustard)]'
                  }`}>
                    {tool.status}
                  </span>
                </div>
                <h3 className="text-xl font-bold text-charcoal-900 mb-2">{tool.label}</h3>
                <p className="text-charcoal-700 text-sm flex-grow mb-4">{tool.description}</p>
                <div className="flex items-center justify-between">
                  <span className={`text-xs font-medium ${colors.text} bg-[var(--bg-raised)] px-2 py-1 rounded`}>
                    {tool.category}
                  </span>
                  <div className="flex items-center gap-2 text-consulting-slate-blue-600 group-hover:text-consulting-slate-blue-700 font-medium text-sm">
                    {tool.status === 'ready' ? 'Launch' : 'Preview'} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Integration with Other Engines */}
      <div className="bg-gradient-to-r from-sage-50 to-ocean-50 p-8 border-2 border-sage-200">
        <h2 className="text-2xl font-bold text-charcoal-900 mb-4">Engine Integration</h2>
        <p className="text-charcoal-700 mb-6">
          Studio Engine works seamlessly with other Peak Priorities engines to create a complete 
          asset management and deployment pipeline.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-[var(--bg-raised)] p-4 border border-sage-200">
            <h3 className="font-bold text-sage-900 mb-2">Assembly Engine</h3>
            <p className="text-charcoal-700 text-sm mb-3">
              Automatically composes assets created in Studio into complete deliverables
            </p>
            <div className="flex items-center gap-2 text-sage-600 text-sm">
              <Settings className="h-4 w-4" />
              <span>Auto-composition</span>
            </div>
          </div>
          <div className="bg-[var(--bg-raised)] p-4 border border-sage-200">
            <h3 className="font-bold text-sage-900 mb-2">Media Engine</h3>
            <p className="text-charcoal-700 text-sm mb-3">
              Optimizes and serves Studio assets with advanced caching and CDN integration
            </p>
            <div className="flex items-center gap-2 text-sage-600 text-sm">
              <BarChart3 className="h-4 w-4" />
              <span>Performance optimization</span>
            </div>
          </div>
          <div className="bg-[var(--bg-raised)] p-4 border border-sage-200">
            <h3 className="font-bold text-sage-900 mb-2">Authoring Engine</h3>
            <p className="text-charcoal-700 text-sm mb-3">
              Integrates Studio assets into content creation workflows and templates
            </p>
            <div className="flex items-center gap-2 text-sage-600 text-sm">
              <Edit3 className="h-4 w-4" />
              <span>Content integration</span>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="bg-charcoal-50 border-2 border-charcoal-200 p-6 text-charcoal-700">
        <h2 className="text-xl font-bold mb-4">Studio Engine Statistics</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-consulting-slate-blue-600">{studioTools.length}</div>
            <div className="text-sm">Total Tools</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-sage-600">{studioTools.filter(t => t.status === 'ready').length}</div>
            <div className="text-sm">Ready for Use</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-[var(--mustard)]">{studioTools.filter(t => t.status === 'development').length}</div>
            <div className="text-sm">In Development</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-coral-600">{assetPipeline.length}</div>
            <div className="text-sm">Pipeline Stages</div>
          </div>
        </div>
      </div>
    </div>
  );
};
